import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { authService } from '../../services/api'
import GoogleLoginButton from './GoogleLoginButton'
import './LoginPage.css'

function userHasErpAccess(user) {
  if (!user) return false
  if (String(user.role || '').toLowerCase() === 'admin') return true
  return Boolean(user.access_group_id)
}

/**
 * Login nativo do ERP — independente da loja Next.
 */
function LoginPage() {
  const { login, loginWithGoogle, logout, isAuthenticated, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleClientId, setGoogleClientId] = useState('')
  const [googleLoading, setGoogleLoading] = useState(false)

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'ERP'
    let active = true
    authService.googleConfig()
      .then((response) => {
        if (!active) return
        const clientId = response?.data?.enabled ? response.data.clientId : ''
        setGoogleClientId(clientId || '')
      })
      .catch((error) => {
        console.warn('Login com Google indisponível', error?.message || error)
      })
    return () => {
      active = false
      document.title = previousTitle
    }
  }, [])

  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-container login-container--status">
          <Loader2 className="login-spinner" size={28} aria-hidden />
          <p>Carregando…</p>
        </div>
      </div>
    )
  }

  if (isAuthenticated && userHasErpAccess(user)) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(email.trim(), password)
      if (!result.success) {
        setError(result.message || 'Erro ao fazer login')
        return
      }

      const sessionUser = result.user
      if (!userHasErpAccess(sessionUser)) {
        logout()
        setError(
          'Sua conta não tem permissão para acessar o ERP. Solicite acesso ao administrador.'
        )
        return
      }

      navigate('/', { replace: true })
    } catch {
      setError('Erro inesperado. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleCredential = async (credential) => {
    setError('')
    setGoogleLoading(true)
    try {
      const result = await loginWithGoogle(credential)
      if (!result.success) {
        setError(result.message || 'Não foi possível entrar com o Google')
        return
      }
      if (!userHasErpAccess(result.user)) {
        logout()
        setError('Sua conta não tem permissão para acessar o ERP. Solicite acesso ao administrador.')
        return
      }
      navigate('/', { replace: true })
    } catch {
      setError('Erro inesperado. Tente novamente.')
    } finally {
      setGoogleLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-shell">
        <div className="login-brand-panel" aria-hidden="true">
          <p className="login-brand-kicker">Acesso</p>
          <h1 className="login-brand-title">ERP</h1>
          <p className="login-brand-copy">
            Entre com a conta da sua organização.
          </p>
        </div>

        <div className="login-container">
          <div className="login-header">
            <p className="login-brand-mark">ERP</p>
            <h2>Entrar</h2>
            <p>Use o e-mail e a senha da sua conta.</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-form-group">
              <label htmlFor="erp-login-email">E-mail</label>
              <input
                id="erp-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="seu@email.com"
                disabled={loading}
              />
            </div>

            <div className="login-form-group">
              <label htmlFor="erp-login-password">Senha</label>
              <div className="login-password-wrap">
                <input
                  id="erp-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error ? (
              <p className="login-error" role="alert">
                {error}
              </p>
            ) : null}

            <button type="submit" className="login-submit" disabled={loading || googleLoading}>
              {loading ? (
                <>
                  <Loader2 className="login-spinner" size={18} aria-hidden />
                  Entrando…
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight size={18} aria-hidden />
                </>
              )}
            </button>
          </form>
          <GoogleLoginButton
            clientId={googleClientId}
            disabled={loading || googleLoading}
            onCredential={handleGoogleCredential}
          />
        </div>
      </div>
    </div>
  )
}

export default LoginPage
